import { Component, computed, input, output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { IFunpayOnboardingIds, IFunpayOnboardingState, isFunpayLive } from '../../../../../services/funpay.service';

type StatusTone = 'progress' | 'failed' | 'submitted' | 'live';

@Component({
    selector: 'funpay-onboarding-status',
    templateUrl: './onboarding-status.html',
    imports: [ButtonModule, TagModule, TranslatePipe, DatePipe]
})
export class FunPayOnboardingStatusComponent {
    state = input<IFunpayOnboardingState | null>(null);
    ids = input<IFunpayOnboardingIds | null>(null);
    busy = input<boolean>(false);
    actionError = input<string | null>(null);
    /** Superadmin only; the server enforces it too. */
    canResync = input<boolean>(false);

    retry = output<void>();
    continueOnboarding = output<void>();
    editDetails = output<void>();
    resync = output<void>();

    verification = computed(() => this.ids()?.verification ?? null);

    /** Adyen has both approved the account and allowed it to take payments. */
    isLive = computed(() => isFunpayLive(this.verification()));

    tone = computed<StatusTone>(() => {
        if (this.isLive()) {
            return 'live';
        }
        if (this.state()?.status === 'FAILED') {
            return 'failed';
        }
        if (this.ids()?.onboardingStatus === 'COMPLETED') {
            return 'submitted';
        }
        return 'progress';
    });

    /** Translation key for the step the application last touched. */
    stepKey = computed(() => {
        const step = this.state()?.step;
        return step ? `FUNPAY_ONBOARDING_STEP_${step}` : '';
    });

    // Adyen has not approved anything at this point; we have only finished provisioning,
    headlineKey = computed(() => {
        switch (this.tone()) {
            case 'live':
                return 'FUNPAY_STATUS_LIVE_TITLE';
            case 'failed':
                return 'FUNPAY_STATUS_FAILED_TITLE';
            case 'submitted':
                return 'FUNPAY_STATUS_SUBMITTED_TITLE';
            default:
                return 'FUNPAY_STATUS_IN_PROGRESS_TITLE';
        }
    });

    bodyKey = computed(() => {
        switch (this.tone()) {
            case 'live':
                return 'FUNPAY_STATUS_LIVE_BODY';
            case 'failed':
                return 'FUNPAY_STATUS_FAILED_BODY';
            case 'submitted':
                return 'FUNPAY_STATUS_SUBMITTED_BODY';
            default:
                return 'FUNPAY_STATUS_IN_PROGRESS_BODY';
        }
    });

    icon = computed(() => {
        switch (this.tone()) {
            case 'live':
                return 'ph-fill ph-check-circle text-paid';
            case 'failed':
                return 'ph-fill ph-warning-circle text-red-500';
            case 'submitted':
                return 'ph-fill ph-check-circle text-paid';
            default:
                return 'ph-fill ph-hourglass-medium text-text-2';
        }
    });

    tag = computed(() => {
        switch (this.tone()) {
            case 'live':
                return { labelKey: 'FUNPAY_STATUS_TAG_LIVE', severity: 'success' as const };
            case 'failed':
                return { labelKey: 'FUNPAY_STATUS_TAG_FAILED', severity: 'danger' as const };
            case 'submitted':
                return { labelKey: 'FUNPAY_STATUS_TAG_SUBMITTED', severity: 'success' as const };
            default:
                return { labelKey: 'FUNPAY_STATUS_TAG_IN_PROGRESS', severity: 'warn' as const };
        }
    });

    /** The account references worth showing, so support has something to quote. */
    references = computed(() => {
        const ids = this.ids();
        if (!ids) {
            return [];
        }

        return [
            { labelKey: 'FUNPAY_REFERENCE_LEGAL_ENTITY', value: ids.legalEntityId },
            { labelKey: 'FUNPAY_REFERENCE_ACCOUNT_HOLDER', value: ids.accountHolderId },
            { labelKey: 'FUNPAY_REFERENCE_BALANCE_ACCOUNT', value: ids.balanceAccountIds?.[0] ?? '' },
            { labelKey: 'FUNPAY_REFERENCE_STORE', value: ids.storeIds?.[0] ?? '' }
        ].filter((reference) => !!reference.value);
    });

    canContinue = computed(() => !!this.ids()?.legalEntityId);

    // Once a legal entity exists the details are Adyen's to change, not ours.
    canEditDetails = computed(() => !this.ids()?.legalEntityId);
}
